// src/components/CardV2/media/ImmersiveCard.tsx
/**
 * ImmersiveCard — full-bleed hero image card for non-canonical content.
 * Image fills the background, header overlaid at top, content anchored to bottom.
 * Falls back to gradient+noise when no image is available.
 */

import React, { FC, useState } from 'react';
import { styled } from 'linaria/react';

import { FONTS } from '../../../styles/constants';
import { LayoutCardProps } from '../types';
import { deriveGradient } from '../primitives/colorUtils';
import { LAYOUT_CARD } from '../styled/tokens';

const StyledContainer = styled.div`
  position: relative;
  width: 100%;
  height: ${LAYOUT_CARD.IMMERSIVE_HEIGHT}px;
  overflow: hidden;
`;

const StyledHeroImage = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const StyledGradientFallback = styled.div<{ gradientFrom: string; gradientTo: string }>`
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, ${({ gradientFrom }) => gradientFrom} 0%, ${({ gradientTo }) => gradientTo} 100%);
`;

const NOISE_DATA_URI = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100' height='100' filter='url(%23n)' opacity='0.15'/%3E%3C/svg%3E")`;

const StyledNoiseOverlay = styled.div`
  position: absolute;
  inset: 0;
  background-image: ${NOISE_DATA_URI};
  background-repeat: repeat;
  opacity: ${LAYOUT_CARD.NOISE_OPACITY};
  pointer-events: none;
`;

const StyledDarkOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, ${LAYOUT_CARD.OVERLAY_OPACITY});
`;

const StyledHeaderWrapper = styled.div`
  position: absolute;
  top: ${LAYOUT_CARD.IMMERSIVE_PADDING.top}px;
  left: ${LAYOUT_CARD.IMMERSIVE_PADDING.sides}px;
  right: ${LAYOUT_CARD.IMMERSIVE_PADDING.sides}px;
  z-index: 1;
`;

const StyledBottomContent = styled.div`
  position: absolute;
  bottom: ${LAYOUT_CARD.IMMERSIVE_PADDING.bottom}px;
  left: ${LAYOUT_CARD.IMMERSIVE_PADDING.sides}px;
  right: ${LAYOUT_CARD.IMMERSIVE_PADDING.sides}px;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const StyledEyebrow = styled.div`
  font-family: ${FONTS.secondary};
  font-size: ${LAYOUT_CARD.EYEBROW_FONT_SIZE}px;
  font-weight: 400;
  line-height: 1.4;
  color: white;
`;

const StyledTitle = styled.div`
  font-family: ${FONTS.primary};
  font-size: ${LAYOUT_CARD.TITLE_FONT_SIZE.immersive}px;
  font-weight: 400;
  line-height: 1.3;
  color: white;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const StyledContentHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const StyledSourceDomain = styled.div`
  font-family: ${FONTS.secondary};
  font-size: 12px;
  font-weight: 400;
  line-height: 1.4;
  color: rgba(255, 255, 255, ${LAYOUT_CARD.SOURCE_TEXT_OPACITY});
  flex: 1;
`;

const StyledFooterRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  height: 32px;
  width: 100%;
`;

const StyledLink = styled.a`
  display: block;
  text-decoration: none;
  cursor: pointer;
  color: inherit;
`;

const ImmersiveCard: FC<LayoutCardProps> = ({
  title,
  imageUrl,
  backgroundColor,
  externalLink,
  eyebrow,
  sourceDomain,
  renderHeader,
  renderFooter,
  className,
}) => {
  const gradient = deriveGradient(backgroundColor);
  const [imgError, setImgError] = useState(false);
  const showImage = !!imageUrl && !imgError;

  const cardBody = (
    <StyledContainer className={className}>
      {showImage ? (
        <StyledHeroImage src={imageUrl as string} alt={title} loading="lazy" onError={() => setImgError(true)} />
      ) : (
        <>
          <StyledGradientFallback gradientFrom={gradient.from} gradientTo={gradient.to} />
          <StyledNoiseOverlay />
        </>
      )}

      <StyledDarkOverlay />

      {renderHeader && <StyledHeaderWrapper>{renderHeader(backgroundColor)}</StyledHeaderWrapper>}

      <StyledBottomContent>
        <StyledContentHeader>
          {eyebrow && <StyledEyebrow>{eyebrow}</StyledEyebrow>}
          <StyledTitle>{title}</StyledTitle>
        </StyledContentHeader>

        {renderFooter && (
          <StyledFooterRow>
            {sourceDomain && <StyledSourceDomain>{sourceDomain}</StyledSourceDomain>}
            {renderFooter('white')}
          </StyledFooterRow>
        )}
      </StyledBottomContent>
    </StyledContainer>
  );

  return externalLink ? (
    <StyledLink href={externalLink} target="_blank" rel="noopener noreferrer">
      {cardBody}
    </StyledLink>
  ) : (
    cardBody
  );
};

export default ImmersiveCard;
